import { noise1, pulse, seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, fade, fill, place, playerColor, sprite,
} from './common';
import phaseFlame from './phaseFlame.png';
import phasePlate from './phasePlate.png';
import phaseSword from './phaseSword.png';

/**
 * **フェイズの切り替え**（Issue #551 v3。指示書 `../direction/new_phase.md`、絵 `../look/new_phase-key.png`）。
 *
 * 文字の出方は全フェイズ共通: ターンプレイヤーの側の画面の外から鋼の板が滑り込み、**急停止 → ヒットストップ**、
 * 少し膨らんで見得を切り、**縦に潰れて進行方向へ抜ける**。
 *
 * ```
 * ふだん（500ms。紋章のすぐ下）         バトル（2000ms。画面の中央）
 * 0    板が滑り込む                     0    板が滑り込む・炎の板が何枚も下から突き上がる
 * 80   急停止・名前 → 止め 100ms        80   急停止・名前 → 止め 100ms
 * 180  揺れ（小）・見得                 180  揺れ・見得。炎は位相をずらして揺れ、火の粉（硬い菱形）が巻き上がる
 * 400  潰れて抜ける（500 で消える）      560  左の剣が弧を描いて飛んできて板の左端に「かちん」（火花・小さな揺れ）
 *                                       820  右の剣が同じく右端に「かちん」。剣は板の両端に刺さったまま
 *                                       1820 板が潰れ、剣は外へ弾け、炎は下へ落ちる（2000 で消える）
 * ```
 *
 * 素材は Gemini で生成（`../fx/phasePlate.png` / `phaseSword.png` / `phaseFlame.png`。緑を抜いたもの）。
 * もやもやした光は使わない（火花は三角の硬い形、閃光は止めの頭の 2 フレームだけ）。
 * **舞台は素に戻す**（`'reset'`。バトルの仮想フィールドが次のフェイズに残らないように）。
 */
const NORMAL = 500;
const BATTLE = 2000;
const HIT = 80;            // 板が止まる
const STOP = 100;          // ヒットストップ
const OUT = { normal: 400, battle: 1820 };
const CLINK = [560, 820];   // バトル: 剣が左 → 右の順に板へ当たる
const FLY_MS = 300;         // 剣が弧を描いて飛ぶ時間

const PLATE_ASPECT = 1400 / 325;
const SWORD_ASPECT = 1400 / 417;
const FLAME_ASPECT = 1400 / 483;

/** ヒットストップの時間の写し（止めの後の動きは STOP だけ遅れる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));

/**
 * 共通の板と文字。`side` は入ってくる側（-1 = 左から、+1 = 右から）。
 * 返す `update(t)` は板・文字・色の線・流れる線を t で決める。
 */
function phasePlateParts(ctx, root, { label, color, side, cx, cy, width, outAt, end, order, nudge = null }) {
  const { kit, THREE } = ctx;
  const { w: W } = canvasOf(ctx);
  const h = width / PLATE_ASPECT;
  const group = new THREE.Group();
  add(root, group, order);
  const plate = sprite(ctx, kit.urlTexture(phasePlate), width, h);
  // 叩きつけの 2 フレームだけ板の形の白（硬い閃光）
  const flash = sprite(ctx, kit.urlTexture(phasePlate), width, h, { blend: 'add' });
  // プレイヤーの色の縁（板の下の辺に沿う硬い線）
  const edge = fill(ctx, color, width * 0.74, 7);
  edge.position.set(0, h * 0.5 + 2, 0);
  const word = kit.metalTextTexture(label, { size: 150, edge: null });
  const pad = Math.max(60, 150 * 0.36 + 10);
  const fit = Math.min(1, (width * 0.6) / Math.max(1, word.width - pad * 2), (h * 0.55) / (150 * 1.3));
  const title = sprite(ctx, word.texture, word.width * fit, word.height * fit);
  title.position.set(0, -2, 0);
  group.add(edge); group.add(plate); group.add(flash); group.add(title);
  edge.renderOrder = order; plate.renderOrder = order + 0.1; flash.renderOrder = order + 0.2; title.renderOrder = order + 0.3;

  // 滑り込むときの流れる線（白の硬い線 3 本。板の後ろに尾を引く）
  const trails = [-0.32, 0.05, 0.36].map((k, i) => ({ m: add(root, fill(ctx, i === 1 ? color : '#ffffff', 1, 1), order - 0.5), k }));
  const x0 = cx + side * (W / 2 + width * 0.7);

  return (t) => {
    const u = hs(t);
    const inK = tween(t, 0, HIT, 'power2_in');         // 加速して急停止
    const mie = 1 + 0.045 * tween(u, HIT, outAt - STOP, 'ease_out');
    const sq = tween(t, outAt, end - 12, 'power3_in');
    const sy = Math.max(0.001, 1 - sq);
    const sx = 1 + 0.9 * sq;
    const x = x0 + (cx - x0) * inK - side * width * 0.45 * sq;   // 抜けるときは進行方向（入ってきた向きのまま）へ
    const [nx, ny] = nudge ? nudge(t) : [0, 0];
    const shown = t < end - 10;
    place(group, x + nx, cy + ny, { sx: mie * sx, sy: mie * sy });
    fade(group, shown ? 1 : 0);
    fade(title, shown && t >= HIT ? 1 : 0);   // 名前は止まった瞬間に出る
    fade(flash, t >= HIT && t < HIT + 34 ? 0.55 : 0);
    // 流れる線: 板の端から入ってきた側へ伸び、止まったら縮んで消える
    const len = (t < HIT ? W * 0.5 * seg(t, 0, 40) : W * 0.5 * (1 - seg(u, HIT, HIT + 90)));
    trails.forEach(({ m, k }) => {
      const tail = x + side * (width / 2);
      place(m, tail + side * len / 2, cy + k * h, { sx: Math.max(0.001, len), sy: k === 0.05 ? 5 : 3 });
      fade(m, len > 1 ? 0.9 : 0);
    });
  };
}

/** 星形の硬い火花（細い三角を放射状に並べる。中は白） */
function starBurst(ctx, rand, radius) {
  const { THREE, kit } = ctx;
  const g = new THREE.Group();
  const spikes = 9;
  const tri = (col, len, wid, a) => {
    const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
    m.material.color?.set?.(col);
    // CircleGeometry(1, 3) は +x に頂点がある。中心から外へ尖らせる
    m.scale.set(len, wid, 1);
    m.position.set(Math.cos(a) * len * 0.45, Math.sin(a) * len * 0.45, 0);
    m.rotation.z = a;
    return m;
  };
  const a0 = rand() * Math.PI * 2;
  for (let i = 0; i < spikes; i += 1) {
    const a = a0 + (i / spikes) * Math.PI * 2 + (rand() - 0.5) * 0.3;
    const len = radius * (0.6 + rand() * 0.6);
    g.add(tri('#ffd21a', len, radius * 0.16, a));
  }
  for (let i = 0; i < spikes; i += 1) {
    const a = a0 + ((i + 0.5) / spikes) * Math.PI * 2;
    g.add(tri('#fff7c2', radius * (0.3 + rand() * 0.25), radius * 0.1, a));
  }
  const core = new THREE.Mesh(new THREE.CircleGeometry(radius * 0.22, 7), kit.plain(null, 1));
  core.material.color?.set?.('#ffffff');
  g.add(core);
  return g;
}

/** フェイズの切り替えの組み立て。`battle` = バトルフェイズ（炎と剣。2000ms）/ false = それ以外（鋼の板。500ms） */
export const newPhaseSpec = (battle) => ({
  label: 'フェイズの切り替え',
  color: '#5ad7ff',
  duration: ({ payload }) => (battle ? BATTLE : NORMAL),
  // 攻撃宣言が盤に置いた矢印と小さいパネルはバトルが終わったら消す（Issue #552）
  clears: () => [{ mark: 'attackArrow' }, { mark: 'battlePanel' }],
  scene: ({ payload }) => (battle
    ? ['reset',
      {
        ms: HIT,
        grade: {
          contrast: { keys: [{ ms: 0, value: 1.4 }, { ms: STOP, value: 1.4, ease: 'hold' }, { ms: STOP + 1, value: 1 }] },
          chroma: { keys: [{ ms: 0, value: 0.014 }, { ms: STOP + 120, value: 0, ease: 'ease_out' }] },
        },
      },
      { ms: HIT + STOP, shake: { amp: 24, freq: 24, decayMs: 300 } },
      ...CLINK.map((ms) => ({ ms, shake: { amp: 14, freq: 30, decayMs: 200 } }))]
    : ['reset', { ms: HIT + STOP, shake: { amp: 9, freq: 26, decayMs: 160 } }]),
  sounds: ({ payload }) => (battle
    ? [{ at: 0, name: 'battle_phase' }, { at: HIT - 5, name: 'set_card', gain: 0.7 },
      ...CLINK.map((ms) => ({ at: ms - 10, name: 'attack', gain: 0.8 }))]
    : [{ at: 0, name: 'phase_change', gain: 0.8 }, { at: HIT - 5, name: 'set_card', gain: 0.55 }]),
  hud: ({ payload, localMs }) => (battle
    ? [{ anchor: 'turnBadge', pulse: Math.max(pulse(localMs, { at: HIT, inMs: 20, hold: 100, outMs: 300 }), ...CLINK.map((at) => 0.6 * pulse(localMs, { at, inMs: 10, outMs: 180 }))), flash: 0.6 * pulse(localMs, { at: HIT, inMs: 0, outMs: 240 }), color: '#ff5a1e' }]
    : [{ anchor: 'turnBadge', pulse: 0.5 * pulse(localMs, { at: HIT, inMs: 20, outMs: 200 }) }]),

  // 層は状況ごとに 1 つ（バトルは炎と剣、ふだんは鋼の板。Issue #589 で状況ごとのファイルに分かれた）
  layers: [
    {
      id: 'battle',
      label: 'バトルフェイズ（炎の板・板・左右の剣）',
      space: 'screen',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const { w: W, h: H } = canvasOf(ctx);
        const p = (ctx.payload.turnPlayer ?? ctx.payload.player) === 1 ? 1 : 0;
        const root = new THREE.Group();
        const end = BATTLE;
        const out = OUT.battle;
        const rand = seeded(`${ctx.seed}:battle`);

        const dim = add(root, place(fill(ctx, '#050303', W, H), W / 2, H / 2), 0);

        /*
         * --- 炎: 形と色の段の違う板を重ね、それぞれ 12 コマで位相をずらして揺らす。
         * 数コマごとに左右を反転して**形が入れ替わる**（アニメの炎の描き替え）。下の辺は常に画面の下の端
         */
        const flameTex = kit.urlTexture(phaseFlame);
        const fw = W * 1.1;
        const fh = fw / FLAME_ASPECT;
        const flameLayers = [
          { h: fh * 1.02, w: fw * 1.15, x: W / 2, tint: '#6e0d07', every: 3, phase: 0, delay: 0, amp: 0.1, order: 1 },
          { h: fh * 0.86, w: fw, x: W / 2 + 60, tint: '#e0461a', every: 2, phase: 1, delay: 40, amp: 0.09, order: 1.1 },
          { h: fh * 0.72, w: fw * 0.95, x: W / 2 - 40, tint: null, every: 4, phase: 2, delay: 80, amp: 0.08, order: 1.2 },
          { h: fh * 0.44, w: W * 0.62, x: W * 0.2, tint: null, every: 2, phase: 3, delay: 130, amp: 0.14, order: 1.3 },
          { h: fh * 0.4, w: W * 0.62, x: W * 0.8, tint: null, every: 3, phase: 5, delay: 150, amp: 0.14, order: 1.3 },
        ].map((f) => {
          const m = add(root, sprite(ctx, flameTex, f.w, f.h), f.order);
          if (f.tint) m.material.color?.set?.(f.tint);
          return { ...f, m };
        });

        // 火の粉（硬い菱形。下から巻き上がり、抜ける前に全部燃え尽きる）
        const emberCols = ['#ffd21a', '#ff8a1a', '#fff3b0', '#ff5a14'];
        const embers = Array.from({ length: 64 }, (_, i) => {
          const m = new THREE.Mesh(new THREE.CircleGeometry(1, 4), kit.plain(null, 1));
          m.material.color?.set?.(emberCols[i % emberCols.length]);
          add(root, m, 1.5);
          const born = 120 + rand() * (out - 120 - 700);
          return { m, i, born, life: 450 + rand() * 250, x: rand() * W, vy: 520 + rand() * 520, size: 8 + rand() * 10, sway: 30 + rand() * 60, spin: (rand() - 0.5) * 16 };
        });

        // --- 板（中央）
        const cx = W / 2; const cy = H * 0.5;
        const PW = 1260;
        const PH = PW / PLATE_ASPECT;
        /** 剣が当たった瞬間に板が当たった側と逆へ少し押される */
        const nudge = (t) => {
          let dx = 0; let dy = 0;
          CLINK.forEach((at, i) => {
            if (t < at) return;
            const k = Math.max(0, 1 - (t - at) / 160);
            const d = Math.sin((t - at) / 18) * k;
            dx += (i === 0 ? 1 : -1) * 12 * d;
            dy += 5 * d;
          });
          return [dx, dy];
        };
        const plateUpdate = phasePlateParts(ctx, root, {
          label: 'BATTLE PHASE', color: playerColor(ctx, p), side: p === 1 ? 1 : -1,
          cx, cy, width: PW, outAt: out, end, order: 8, nudge,
        });

        /*
         * --- 剣（小さく、板の左右に 1 本ずつ）。画面の上の外から回りながら弧を描いて飛んできて、
         * 切っ先が板の端に「かちん」と当たり、そのまま刺さった形で残る（左 → 右の順）
         */
        const SW = 470;
        const SH = SW / SWORD_ASPECT;
        const swordTex = kit.urlTexture(phaseSword);
        const TILT = 0.62;   // 切っ先を内側の下へ
        const swords = [-1, 1].map((s, i) => {
          const rot = s === -1 ? TILT : -TILT;
          const dir = [-s * Math.cos(TILT), Math.sin(TILT)];         // 柄 → 切っ先
          const tip = [cx + s * PW * 0.43, cy + PH * 0.08];
          const home = [tip[0] - dir[0] * SW * 0.44, tip[1] - dir[1] * SW * 0.44];
          const from = [cx + s * (W / 2 + 420), -380];
          const ctrl = [cx + s * (W / 2 + 60), cy - 760];
          const m = add(root, sprite(ctx, swordTex, SW, SH), 9 + i * 0.1);
          return { m, s, rot, dir, tip, home, from, ctrl, at: CLINK[i] };
        });

        // 当たった所の星形の火花と直線の火花（剣ごと）
        const hits = swords.map((sw) => {
          const star = starBurst(ctx, rand, 120);
          add(root, star, 10);
          const lines = Array.from({ length: 12 }, (_, i) => {
            const a = -Math.PI / 2 + sw.s * 0.3 + (rand() - 0.5) * Math.PI * 1.4;
            return { m: add(root, fill(ctx, ['#ffffff', '#ffd54a', '#ffb020'][i % 3], 1, 1), 10), a, len: 60 + rand() * 110, w: 3 + rand() * 3, speed: 700 + rand() * 900 };
          });
          return { ...sw, star, lines, spin: (rand() - 0.5) * 1.5 };
        });

        const flash = add(root, place(fill(ctx, '#ffffff', W, H, { blend: 'add' }), W / 2, H / 2), 12);

        return {
          root,
          update: ({ localMs: t }) => {
            const leave = seg(t, out, end - 10);
            fade(dim, 0.62 * seg(t, 0, HIT) * (1 - leave));

            // 炎
            const drop = tween(t, out, end - 10, 'power2_in');
            flameLayers.forEach((f, i) => {
              const step = Math.floor((t + f.phase * 29) / 83);            // 12 コマ
              const shape = Math.floor(step / f.every) + f.phase;          // 数コマごとに描き替え
              const flip = shape % 2 === 0 ? 1 : -1;
              const n = noise1(`${ctx.seed}:flame${i}`, step * 0.9);
              const up = tween(t, f.delay, f.delay + 220, 'expo_out');
              const sy = 1 + f.amp * n;
              const hh = f.h * sy;
              const y = H + hh / 2 - hh * up + hh * 1.1 * drop;
              place(f.m, f.x + 18 * noise1(`${ctx.seed}:fx${i}`, step * 0.7), y, { sx: flip * (1 - 0.03 * n), sy });
              fade(f.m, t < end - 10 ? 1 : 0);
            });
            embers.forEach((e) => {
              const k = (t - e.born) / e.life;
              if (k < 0 || k >= 1) { fade(e.m, 0); return; }
              const age = (t - e.born) / 1000;
              const step = Math.floor(t / 83);
              const x = e.x + e.sway * noise1(`${ctx.seed}:em${e.i}`, step * 0.5);
              const y = H + 20 - e.vy * age;
              place(e.m, x, y, { sx: e.size * (1 - k), sy: e.size * 1.6 * (1 - k), rot: e.spin * age });
              fade(e.m, 1);
            });

            plateUpdate(t);

            // 剣
            hits.forEach((sw) => {
              const { m, s, rot, dir, home, from, ctrl, at } = sw;
              const k = tween(t, at - FLY_MS, at, 'power2_in');
              let x; let y; let r;
              if (t < at) {
                // 二次ベジェの弧。回りながら飛ぶ（最後の 1/4 回転で切っ先が板へ向く）
                const a = 1 - k;
                x = a * a * from[0] + 2 * a * k * ctrl[0] + k * k * home[0];
                y = a * a * from[1] + 2 * a * k * ctrl[1] + k * k * home[1];
                r = rot - s * Math.PI * 2.25 * (1 - k);
              } else {
                // 当たった反動で少し跳ね返り、刺さって落ち着く
                const b = Math.sin(seg(t, at, at + 130) * Math.PI) * (1 - seg(t, at, at + 130));
                x = home[0] - dir[0] * 16 * b;
                y = home[1] - dir[1] * 16 * b;
                r = rot - s * 0.07 * b;
              }
              // はける: 外の上へ弾かれる
              const o = tween(t, out, end - 10, 'power2_in');
              x += s * 900 * o; y -= 500 * o; r += s * 3 * o;
              place(m, x, y, { sx: s === 1 ? -1 : 1, rot: r });
              fade(m, t >= at - FLY_MS && t < end - 10 ? 1 : 0);

              // 火花
              const [tx, ty] = sw.tip;
              const sk = seg(t, at, at + 180);
              place(sw.star, tx, ty, { s: Math.max(0.001, t < at ? 0 : 1.1 * (1 - sk)), rot: sw.spin * sk });
              fade(sw.star, t >= at && sk < 1 ? 1 : 0);
              const since = Math.max(0, (t - at) / 1000);
              sw.lines.forEach((l) => {
                const lk = Math.min(1, since / 0.3);
                const rr = 30 + l.speed * since * (1 - 0.4 * lk);
                const len = l.len * (1 - lk);
                l.m.position.set(tx + Math.cos(l.a) * (rr + len / 2), ty + Math.sin(l.a) * (rr + len / 2), 0);
                l.m.rotation.z = l.a;
                l.m.scale.set(Math.max(0.001, len), l.w, 1);
                fade(l.m, t >= at && lk < 1 ? 1 : 0);
              });
            });

            // 閃光（板が止まった頭の 2 フレームだけ）
            fade(flash, t >= HIT && t < HIT + 34 ? 0.14 : 0);
          },
        };
      },
    },
    {
      id: 'plate',
      label: 'ふだんのフェイズ（鋼の板と名前）',
      space: 'screen',
      script: (ctx) => {
        const { THREE } = ctx;
        const { w: W } = canvasOf(ctx);
        const p = (ctx.payload.turnPlayer ?? ctx.payload.player) === 1 ? 1 : 0;
        const root = new THREE.Group();
        // 紋章のすぐ下（HUD のフェイズ名よりさらに下）
        const badge = ctx.stage?.hud?.turnBadge?.() ?? null;
        const cy = (badge ? badge.y + badge.h : 150) + 170;
        const update = phasePlateParts(ctx, root, {
          label: String(ctx.payload.phaseLabel).toUpperCase(), color: playerColor(ctx, p), side: p === 1 ? 1 : -1,
          cx: W / 2, cy, width: 980, outAt: OUT.normal, end: NORMAL, order: 2,
        });
        return { root, update: ({ localMs: t }) => update(t) };
      },
    },
  ].filter((l) => l.id === (battle ? 'battle' : 'plate')),
});
